import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { accountAPI } from '../../services/api';
import { useMerchantPayoutFlags } from '../../hooks/useMerchantPayoutFlags';
import LogoutModal from '../../components/LogoutModal';
import WalletCreditsModal from '../../components/WalletCreditsModal';
import Footer from '../../components/Footer';

interface MerchantStats {
  walletBalance: number;
  accountBalance: number;
  totalPayoutCount: number;
  todayPayoutCount: number;
  todayCredits: number;
  todayDebits: number;
  todayCharges: number;
  totalPayoutVolume: number;
  pendingPayouts: number;
  completedPayouts: number;
  failedPayouts: number;
}

const MerchantDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { viyonaEnabled, poolpayEnabled } = useMerchantPayoutFlags();
  const [stats, setStats] = useState<MerchantStats>({
    walletBalance: 0,
    accountBalance: 0,
    totalPayoutCount: 0,
    todayPayoutCount: 0,
    todayCredits: 0,
    todayDebits: 0,
    todayCharges: 0,
    totalPayoutVolume: 0,
    pendingPayouts: 0,
    completedPayouts: 0,
    failedPayouts: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    accountNumber: '',
    ifscCode: '',
    amount: ''
  });
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [bankLookup, setBankLookup] = useState<{ loading: boolean; name: string | null }>({ loading: false, name: null });
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);

  // Keep sidebar visible on desktop (overrides Tailwind in production build)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }
    if (user.role !== 'merchant') {
      return;
    }
    fetchMerchantData();
  }, []);

  const fetchMerchantData = async () => {
    try {
      setLoading(true);
      const dashboardResponse = await api.get('/merchants/my-dashboard/stats');
      const dashboardData = dashboardResponse.data.data;

      // Update all stats with real data from API
      setStats({
        walletBalance: dashboardData.wallet_balance || 0,
        accountBalance: dashboardData.account_balance || 0, // Using account_balance from API
        totalPayoutCount: dashboardData.total_payout_count || 0,
        todayPayoutCount: dashboardData.today_payout_count || 0,
        todayCredits: dashboardData.today_credits || 0,
        todayDebits: dashboardData.today_debits || 0,
        todayCharges: dashboardData.today_charges || 0,
        totalPayoutVolume: dashboardData.total_payout_volume || 0,
        pendingPayouts: dashboardData.pending_payouts || 0,
        completedPayouts: dashboardData.completed_payouts || 0,
        failedPayouts: dashboardData.failed_payouts || 0
      });
    } catch {
      // Keep zeros on error
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const merchantSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '🏦' },
    { id: 'payouts', label: 'Payouts', icon: '📋' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    ...(viyonaEnabled ? [{ id: 'create-payout' as const, label: 'Create Payout (v1 Viyona)', icon: '➕' }] : []),
    ...(poolpayEnabled ? [{ id: 'create-payout-v2' as const, label: 'Create Payout (v2 PoolPay)', icon: '💠' }] : []),
    { id: 'logout', label: 'Logout', icon: '🚪' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch {
      /* ignore */
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleCreditMoney = () => {
    setShowCreditsModal(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleAddPayout = () => {
    window.location.href = '/merchant/new-payouts';
  };

  const handleMyProfile = () => {
    window.location.href = '/merchant/account';
  };

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'account') {
      window.location.href = '/merchant/account';
    } else if (tabId === 'payouts') {
      window.location.href = '/merchant/payouts'; // Sidebar goes to payouts list
    } else if (tabId === 'reports') {
      window.location.href = '/merchant/reports';
    } else if (tabId === 'create-payout') {
      window.location.href = '/merchant/new-payouts';
    } else if (tabId === 'create-payout-v2') {
      window.location.href = '/merchant/new-payouts/poolpay';
    } else if (tabId === 'logout') {
      setShowLogoutModal(true);
    }
    // For 'home', stay on current page (/merchant)
    // Close sidebar on mobile after navigation
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-100 text-violet-600">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
        <p className="text-violet-600 text-base m-0">Loading Merchant Dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <div id="merchant-dashboard" className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-100 to-slate-200">
        {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} aria-hidden />}
        <aside
          id="merchant-sidebar"
          className="fixed top-0 left-0 z-50 h-full w-64 bg-[#0b0d32] text-slate-100 shadow-xl transition-transform duration-200 ease-out"
          style={isDesktop ? { transform: 'translateX(0)' } : sidebarOpen ? { transform: 'translateX(0)' } : { transform: 'translateX(-100%)' }}
        >
          <div className="p-5 border-b border-slate-600">
            <div className="flex items-center">
              <img
                src={`${process.env.PUBLIC_URL || ''}/logo4.png`}
                alt="YuvanAPI"
                className="h-[3.75rem] w-auto max-w-[19rem] object-contain object-left"
              />
            </div>
          </div>
          <nav className="p-4 flex flex-col gap-1">
            {merchantSidebarItems.map(item => (
              <button
                key={item.id}
                type="button"
                className={`flex items-center gap-3 w-full py-3 px-4 rounded-lg text-left font-medium transition-colors ${activeTab === item.id ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
                onClick={() => handleNavigation(item.id)}
              >
                <span className="text-xl">{item.icon}</span>
                <span className="leading-tight">
                  {item.id === 'create-payout' ? (
                    <>
                      <span className="block">Create Payout</span>
                      <span className="block text-xs opacity-90">(v1 Viyona)</span>
                    </>
                  ) : item.id === 'create-payout-v2' ? (
                    <>
                      <span className="block">Create Payout</span>
                      <span className="block text-xs opacity-90">(v2 PoolPay)</span>
                    </>
                  ) : (
                    <span className="block">{item.label}</span>
                  )}
                </span>
              </button>
            ))}
          </nav>
        </aside>

        <header id="merchant-mobile-header" className="flex items-center gap-4 p-4 bg-[#0b0d32] text-white shadow sticky top-0 z-30" style={isDesktop ? { display: 'none' } : undefined}>
          <button type="button" className="p-2 rounded-lg hover:bg-slate-700" onClick={toggleSidebar} aria-label="Menu">☰</button>
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo4.png`}
            alt="YuvanAPI"
            className="h-14 w-auto max-w-[17rem] object-contain"
          />
        </header>

        <main id="merchant-main" className="flex-1 p-6 pt-8" style={isDesktop ? { marginLeft: '16rem' } : undefined}>
          <div
            className="flex flex-col gap-4 mb-6"
            style={isDesktop ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' } : undefined}
          >
            <h1 className="text-2xl font-bold text-slate-800 m-0 shrink-0">Welcome to YuvanAPI Merchant!</h1>
            <div
              className="flex flex-wrap gap-2"
              style={{ flexShrink: 0, minWidth: 0 }}
            >
              <button type="button" onClick={handleCreditMoney} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700 whitespace-nowrap shrink-0">💳 CREDIT MONEY</button>
              <button type="button" onClick={handleAddPayout} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 whitespace-nowrap shrink-0">💸 + Payout</button>
              <button type="button" onClick={handleMyProfile} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-slate-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-slate-700 whitespace-nowrap shrink-0">👤 My Profile</button>
              <button type="button" onClick={handleLogoutClick} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-red-700 whitespace-nowrap shrink-0">🚪 Logout</button>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 m-0 mb-2">Hi {user?.name || 'Merchant'}!</h2>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                <span className="text-base font-semibold text-slate-700">Balance Overview</span>
                <span className="text-sm text-slate-500">Live balances from your account</span>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center text-2xl">💼</div>
                <div>
                  <span className="block text-sm text-slate-600">Wallet Balance (Receive from Manager)</span>
                  <span className="text-xl font-bold text-slate-800">{formatCurrency(stats.walletBalance)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: '💰', title: 'Wallet Balance', value: formatCurrency(stats.walletBalance) },
              { icon: '📊', title: 'Total Payout Count', value: stats.totalPayoutCount.toLocaleString() },
              { icon: '👥', title: 'Today Payout Count', value: String(stats.todayPayoutCount) },
              { icon: '⬆️', title: 'Today Credits', value: formatCurrency(stats.todayCredits) },
              { icon: '⬇️', title: 'Today Debits', value: formatCurrency(stats.todayDebits) },
              { icon: '📋', title: 'Today Service Charges', value: formatCurrency(stats.todayCharges) },
              { icon: '📈', title: 'Total Payout Volume', value: formatCurrency(stats.totalPayoutVolume) },
              { icon: '⏳', title: 'Pending Payouts', value: String(stats.pendingPayouts) },
              { icon: '✅', title: 'Completed Payouts', value: String(stats.completedPayouts) },
            ].map((card, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center text-2xl">{card.icon}</div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-slate-600 m-0 mb-1">{card.title}</h3>
                  <p className="text-lg font-bold text-slate-800 m-0 truncate">{card.value}</p>
                </div>
              </div>
            ))}
          </div>
        </main>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      {/* Wallet Credits Modal */}
      <WalletCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={() => setShowWithdrawModal(false)}>
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800 m-0">Redeem Coins</h2>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-200 text-xl cursor-pointer" onClick={() => setShowWithdrawModal(false)}>✕</button>
            </div>
            <div className="p-6">
              {withdrawError && (
                <div className="mb-4 py-3 px-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">❌ {withdrawError}</div>
              )}
              <div className="space-y-4">
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Coin Balance</label>
                  <div className="flex items-center justify-between py-2.5 px-4 rounded-lg border-2 border-slate-200 bg-slate-50 text-slate-800 text-sm">
                    <span className="opacity-80">Available Coins</span>
                    <strong>{Math.floor(stats.accountBalance).toLocaleString()} Coin</strong>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">1 Coin = ₹1</label>
                  <input
                    type="number"
                    placeholder="Enter number of coins to redeem"
                    min={1}
                    step={1}
                    value={withdrawForm.amount}
                    onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                    className="w-full py-2.5 px-4 border-2 border-slate-200 rounded-lg text-slate-800 bg-white text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
                  <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-50 disabled:opacity-50" onClick={() => setShowWithdrawModal(false)} disabled={withdrawing}>Cancel</button>
                  <button
                    type="button"
                    className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-violet-600 text-white border-none hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={withdrawing || Math.floor(stats.accountBalance) <= 0 || !withdrawForm.amount}
                    onClick={async () => {
                      setWithdrawError(null);
                      try {
                        setWithdrawing(true);
                        // Attempt a redeem endpoint if available; otherwise, just close the modal
                        const redeemAmount = Math.floor(parseInt(withdrawForm.amount || '0', 10));
                        if (redeemAmount <= 0) throw new Error('Enter a valid coin amount');
                        if (redeemAmount > Math.floor(stats.accountBalance)) throw new Error('Insufficient coin balance');
                        if (typeof (accountAPI as any).redeemCoins === 'function') {
                          const res = await (accountAPI as any).redeemCoins({ amount: redeemAmount });
                          if (!res.data?.success) {
                            throw new Error(res.data?.message || 'Redeem failed');
                          }
                        }
                        setShowWithdrawModal(false);
                        setStats((prev) => ({ ...prev, accountBalance: Math.max(0, Math.floor(prev.accountBalance) - redeemAmount) }));
                      } catch (err: any) {
                        setWithdrawError(err.response?.data?.message || err.message || 'Redeem failed');
                      } finally {
                        setWithdrawing(false);
                      }
                    }}
                  >
                    {withdrawing ? 'Redeeming...' : 'Redeem'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
      <Footer />
    </>
  );
};

export default MerchantDashboard;
